import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, ImageSourcePropType, StyleSheet, Text, View } from 'react-native';
import Svg, { Defs, LinearGradient, Path, Stop } from 'react-native-svg';

import BonusesIcon from '../../../assets/desktop/dock/bonuses.png';
import HomeIcon from '../../../assets/desktop/dock/home.png';
import HomePlate from '../../../assets/desktop/dock/home-plate.svg';
import HomePlate2 from '../../../assets/desktop/dock/home-plate-2.svg';
import HomePlateBorder from '../../../assets/desktop/dock/home-plate-border.svg';
import HomePlateGlow from '../../../assets/desktop/dock/home-plate-glow.png';
import MissionsIcon from '../../../assets/desktop/dock/missions.png';
import RankingsIcon from '../../../assets/desktop/dock/rankings.png';
import StoreIcon from '../../../assets/desktop/dock/store.png';
import { useSvgId } from '../../../utils/hooks/useSvgId';
import { Pressable } from '../common/Touchable';
import { useDesktopLayout } from './desktopLayout';

// Figma "Bottom Dock" (1625:4590, "mackbook air") and the "Nav Item / Desktop Bar" component
// set (1614:972, page "BOGI DRAFT"). All numbers are in the 1280x832 frame's space.
//
// The dock is 136 high: the raised Home button starts at 0, the notch that holds it at 16, and
// the bar itself runs 56..133. The bar is the full content width - at 1920 it is 1836 wide, not
// 1216 x 1.3 - so the four side items share whatever is left beside the Home slot.
const DOCK = { height: 136, notchTop: 16, barTop: 56, barHeight: 77 } as const;
/** Four side items, 6 apart, and the 131-wide Home slot in the middle. */
const ITEM_GAP = 6;
const HOME = { width: 131.155, height: 135.249 } as const;
// The component set's masters are drawn at 281.5x97; the dock places them at 265x77, which is
// the master scaled by 77/97 - icon, label and gap together.
const MASTER_ITEM_HEIGHT = 97;
const ITEM_SCALE = DOCK.barHeight / MASTER_ITEM_HEIGHT;
const LABEL = { fontSize: 20, lineHeight: 24, gap: 13 } as const;
const SELECTED = { radius: 20, border: 2.12 } as const;

/**
 * The dock base's outline, from Figma "Dock Base" (a union of the 77-high bar and the notch).
 * It is rebuilt here rather than shipped as the exported SVG because the bar stretches with the
 * viewport while the notch keeps its shape, which a single scaled drawing cannot do. `w` is the
 * bar width in pixels, `u` the design-to-pixel factor; the notch is drawn off the centre line.
 */
const dockPath = (w: number, u: (v: number) => number) => {
	const c = w / 2;
	const top = u(DOCK.barTop - DOCK.notchTop);
	const bottom = top + u(DOCK.barHeight);
	const r = u(20);
	const k = u(8.954);
	const x = (dx: number) => c + u(dx);
	const y = (dy: number) => u(dy);

	return [
		`M ${c} 0`,
		`C ${x(15.822)} 0 ${x(30.002)} ${y(6.87)} ${x(39.592)} ${y(17.726)}`,
		`H ${x(43.931)}`,
		`C ${x(55.484)} ${y(17.726)} ${x(64.851)} ${y(26.884)} ${x(64.851)} ${y(38.181)}`,
		`V ${top}`,
		`H ${w - r}`,
		`C ${w - k} ${top} ${w} ${top + (r - k)} ${w} ${top + r}`,
		`V ${bottom - r}`,
		`C ${w} ${bottom - (r - k)} ${w - k} ${bottom} ${w - r} ${bottom}`,
		`H ${x(54.841)}`,
		`C ${x(51.664)} ${bottom + y(1.902)} ${x(47.929)} ${bottom + y(3)} ${x(43.931)} ${bottom + y(3)}`,
		`H ${x(-43.931)}`,
		`C ${x(-47.929)} ${bottom + y(3)} ${x(-51.664)} ${bottom + y(1.902)} ${x(-54.841)} ${bottom}`,
		`H ${r}`,
		`C ${k} ${bottom} 0 ${bottom - (r - k)} 0 ${bottom - r}`,
		`V ${top + r}`,
		`C 0 ${top + (r - k)} ${k} ${top} ${r} ${top}`,
		`H ${x(-64.851)}`,
		`V ${y(38.181)}`,
		`C ${x(-64.851)} ${y(26.884)} ${x(-55.484)} ${y(17.726)} ${x(-43.931)} ${y(17.726)}`,
		`H ${x(-39.592)}`,
		`C ${x(-30.002)} ${y(6.87)} ${x(-15.822)} 0 ${c} 0`,
		'Z',
	].join(' ');
};

export type DockItemKey = 'bonuses' | 'home' | 'missions' | 'rankings' | 'store';

type Props = {
	activeItem: DockItemKey;
	disabledItems?: Array<'missions'>;
	onBonusPress: () => void;
	onHomePress: () => void;
	onMissionsPress: () => void;
	onRankingPress: () => void;
	onStorePress: () => void;
	/** Lights the bonuses badge - a daily bonus is waiting. */
	ready: boolean;
};

const DesktopDock = ({
	activeItem,
	disabledItems = [],
	onBonusPress,
	onHomePress,
	onMissionsPress,
	onRankingPress,
	onStorePress,
	ready,
}: Props) => {
	const { t } = useTranslation();
	const { s, contentWidth } = useDesktopLayout();
	const fillId = useSvgId('dockFill');
	const strokeId = useSvgId('dockStroke');
	const [hovered, setHovered] = useState<DockItemKey | null>(null);
	const barWidth = contentWidth;
	const itemWidth = Math.max(0, (barWidth - s(HOME.width) - s(ITEM_GAP) * 4) / 4);
	const baseHeight = s(DOCK.height - DOCK.notchTop);
	const styles = createStyles(s, itemWidth);

	const sideItems: Array<{
		key: DockItemKey;
		label: string;
		icon: ImageSourcePropType;
		iconSize: { width: number; height: number };
		onPress: () => void;
		badge?: boolean;
	}> = [
		{
			key: 'rankings',
			label: t('T_SITE_RANKING_TITLE', { defaultValue: 'RANKINGS' }),
			icon: RankingsIcon,
			iconSize: { width: 53.943, height: 52.832 },
			onPress: onRankingPress,
		},
		{
			key: 'missions',
			label: t('T_SITE_NAV_MISSIONS', { defaultValue: 'MISSIONS' }),
			icon: MissionsIcon,
			iconSize: { width: 60.289, height: 59.171 },
			onPress: onMissionsPress,
		},
		{
			key: 'bonuses',
			label: t('T_SITE_NAV_BONUSES', { defaultValue: 'BONUSES' }),
			icon: BonusesIcon,
			iconSize: { width: 50.417, height: 52.479 },
			onPress: onBonusPress,
			badge: ready,
		},
		{
			key: 'store',
			label: t('T_SITE_STORE_TITLE', { defaultValue: 'Store' }).toUpperCase(),
			icon: StoreIcon,
			iconSize: { width: 52.533, height: 53.54 },
			onPress: onStorePress,
		},
	];

	const renderSideItem = (item: (typeof sideItems)[number]) => {
		const isActive = activeItem === item.key;
		const isDisabled = disabledItems.includes(item.key as 'missions');
		const isHovered = hovered === item.key && !isActive;
		const u = (v: number) => s(v * ITEM_SCALE);

		return (
			<Pressable
				key={item.key}
				disabled={isDisabled}
				onPress={item.onPress}
				onHoverIn={() => setHovered(item.key)}
				onHoverOut={() => setHovered(current => (current === item.key ? null : current))}
				style={[styles.sideItem, isDisabled && styles.disabled]}
			>
				{isActive ? (
					<View pointerEvents='none' style={styles.selectedPlate}>
						<View style={styles.selectedGlow} />
					</View>
				) : null}
				<View style={[styles.itemRow, { gap: u(LABEL.gap) }]}>
					<View style={{ width: u(item.iconSize.width), height: u(item.iconSize.height) }}>
						<Image source={item.icon} style={StyleSheet.absoluteFill} resizeMode='contain' />
						{item.badge ? (
							// Figma "Badge": a 17.6 dot hanging off the icon's top-right corner.
							<View style={[styles.badge, { left: u(47.2), top: u(-8.3) }]} />
						) : null}
					</View>
					<Text numberOfLines={1} style={[styles.label, isHovered && styles.labelHover]}>
						{item.label}
					</Text>
				</View>
			</Pressable>
		);
	};

	const isHomeActive = activeItem === 'home';

	return (
		<View pointerEvents='box-none' style={[styles.dock, { width: barWidth }]}>
			<Svg pointerEvents='none' width={barWidth} height={baseHeight + s(4)} style={styles.base}>
				<Defs>
					<LinearGradient id={fillId} x1='0' y1='0' x2='0' y2='1'>
						<Stop offset='0' stopColor='#770700' />
						<Stop offset='0.80127' stopColor='#320400' />
					</LinearGradient>
					<LinearGradient id={strokeId} x1='0' y1='1' x2='0' y2='0'>
						<Stop offset='0' stopColor='#DA600E' />
						<Stop offset='1' stopColor='#F46A0C' />
					</LinearGradient>
				</Defs>
				<Path
					d={dockPath(barWidth, s)}
					fill={`url(#${fillId})`}
					stroke={`url(#${strokeId})`}
					strokeWidth={Math.max(1, s(0.81))}
				/>
			</Svg>

			<View pointerEvents='box-none' style={styles.row}>
				{sideItems.slice(0, 2).map(renderSideItem)}
				<View style={styles.homeSlot} />
				{sideItems.slice(2).map(renderSideItem)}
			</View>

			<Pressable
				onPress={onHomePress}
				onHoverIn={() => setHovered('home')}
				onHoverOut={() => setHovered(current => (current === 'home' ? null : current))}
				style={[styles.home, { left: (barWidth - s(HOME.width)) / 2 }]}
			>
				{isHomeActive ? (
					<>
						{/* The plate art is exported with its blur padding: 145.8x139.32 for the
						    129.6x123.12 plate placed 11.34 down. */}
						<View pointerEvents='none' style={styles.homePlateArt}>
							<HomePlate width='100%' height='100%' />
						</View>
						<View pointerEvents='none' style={styles.homePlateArt}>
							<HomePlate2 width='100%' height='100%' />
						</View>
						<View pointerEvents='none' style={styles.homePlateBorder}>
							<HomePlateBorder width='100%' height='100%' />
						</View>
						<Image source={HomePlateGlow} style={styles.homePlateGlow} resizeMode='contain' />
					</>
				) : null}
				<Image source={HomeIcon} style={styles.homeIcon} resizeMode='contain' />
				<Text
					numberOfLines={1}
					style={[styles.homeLabel, hovered === 'home' && !isHomeActive && styles.labelHover]}
				>
					{t('T_SITE_NAV_HOME', { defaultValue: 'HOME' })}
				</Text>
			</Pressable>
		</View>
	);
};

const createStyles = (s: (v: number) => number, itemWidth: number) =>
	StyleSheet.create({
		dock: {
			height: s(DOCK.height),
			alignSelf: 'center',
		},
		base: {
			position: 'absolute',
			left: 0,
			top: s(DOCK.notchTop),
			// Figma: drop shadow 0 6.48 8.1 rgba(255,0,0,0.25) under the whole base.
			filter: `drop-shadow(0px ${s(6.48)}px ${s(4.05)}px rgba(255,0,0,0.25))`,
		} as any,
		row: {
			position: 'absolute',
			left: 0,
			right: 0,
			top: s(DOCK.barTop),
			height: s(DOCK.barHeight),
			flexDirection: 'row',
			columnGap: s(ITEM_GAP),
		},
		sideItem: {
			width: itemWidth,
			height: s(DOCK.barHeight),
			alignItems: 'center',
			justifyContent: 'center',
			cursor: 'pointer',
		} as any,
		disabled: {
			opacity: 0.72,
		},
		itemRow: {
			flexDirection: 'row',
			alignItems: 'center',
		},
		label: {
			color: '#FFFFFF',
			fontFamily: 'Sofia Sans',
			fontWeight: '500',
			fontSize: s(LABEL.fontSize * ITEM_SCALE),
			lineHeight: s(LABEL.lineHeight * ITEM_SCALE),
		},
		// Figma "State=Hover": only the label changes, to the gold hairline colour.
		labelHover: {
			color: '#FFF908',
		},
		// Figma "Nav Plate / Selected" (1614:982).
		selectedPlate: {
			...StyleSheet.absoluteFillObject,
			borderRadius: s(SELECTED.radius * ITEM_SCALE),
			borderWidth: s(SELECTED.border * ITEM_SCALE),
			borderColor: '#FFF908',
			overflow: 'hidden',
			backgroundImage: 'linear-gradient(180deg, #B00E19 19.629%, #7B0007 80.127%)',
			boxShadow: `0 0 ${s(8.1)}px rgba(255,48,0,0.6), inset 0 0 ${s(16.2)}px rgba(255,0,0,0.6)`,
		} as any,
		// Figma "Glow": a 191x49 #FF3015 ellipse under a 22.2 blur. The exported SVG's filter does
		// not survive react-native-svg on web - it paints the ellipse hard - so it is a radial
		// gradient instead.
		selectedGlow: {
			position: 'absolute',
			left: '8%',
			right: '8%',
			top: '10%',
			bottom: '10%',
			backgroundImage:
				'radial-gradient(ellipse at center, rgba(255,48,21,0.75) 0%, rgba(255,48,21,0.35) 45%, rgba(255,48,21,0) 72%)',
		} as any,
		badge: {
			position: 'absolute',
			width: s(17.6 * ITEM_SCALE),
			height: s(17.6 * ITEM_SCALE),
			borderRadius: s(8.8 * ITEM_SCALE),
			backgroundColor: '#E10600',
			borderWidth: Math.max(1, s(1.76 * ITEM_SCALE)),
			borderColor: '#000000',
		},
		homeSlot: {
			width: s(HOME.width),
		},
		home: {
			position: 'absolute',
			top: 0,
			width: s(HOME.width),
			height: s(HOME.height),
			cursor: 'pointer',
		} as any,
		homePlateArt: {
			position: 'absolute',
			left: s(-8.1),
			top: s(11.34 - 8.1),
			width: s(145.8),
			height: s(139.32),
		},
		homePlateBorder: {
			position: 'absolute',
			left: 0,
			top: s(11.34),
			width: s(129.6),
			height: s(123.12),
		},
		homePlateGlow: {
			position: 'absolute',
			left: s(1.62),
			top: s(2.2),
			width: s(126.36),
			height: s(124.74),
			// Figma: plus-lighter at 80%, over an image already at 40%.
			opacity: 0.32,
			mixBlendMode: 'plus-lighter',
		} as any,
		homeIcon: {
			position: 'absolute',
			left: s(27.54),
			top: s(29.59),
			width: s(72.981),
			height: s(70.09),
		},
		homeLabel: {
			position: 'absolute',
			left: 0,
			right: 0,
			top: s(102.23),
			textAlign: 'center',
			color: '#FFFFFF',
			fontFamily: 'Sofia Sans',
			fontWeight: '500',
			fontSize: s(LABEL.fontSize),
			lineHeight: s(LABEL.lineHeight),
		},
	});

export default DesktopDock;
